import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Edit2, Eye, EyeOff, Clock, CalendarCheck, Receipt } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { catalogApi } from '../lib/api';
import { CatalogItemDialog } from './CatalogItemDialog';
import { categoryLabel, formatPrice, formatDuration } from './catalogConstants';

const errorMessage = (err) => {
  const d = err?.response?.data?.detail;
  if (Array.isArray(d)) return 'Valeurs invalides : vérifiez les champs.';
  return d || 'Erreur de sauvegarde';
};

const Badge = ({ children, cls, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>{children}</span>
);

const ItemRow = ({ item, isService, optionNames, onEdit, onToggle }) => (
  <div data-testid={`catalog-row-${item.code}`}
    className={`flex flex-col md:flex-row md:items-center gap-3 p-4 border-b border-slate-100 last:border-0 transition-opacity ${item.active ? '' : 'opacity-60'}`}>
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-bold text-slate-900">{item.name}</p>
        <code className="text-xs text-slate-400">{item.code}</code>
        {isService && <Badge cls="bg-slate-50 text-slate-600 border-slate-200">{categoryLabel(item.category)}</Badge>}
        <Badge testid={`status-${item.code}`} cls={item.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}>
          {item.active ? 'Active' : 'Inactive'}
        </Badge>
        {isService && item.direct_booking && <Badge testid={`direct-badge-${item.code}`} cls="bg-blue-50 text-blue-700 border-blue-200"><CalendarCheck className="w-3 h-3" />Réservation directe</Badge>}
        {isService && item.tax_credit_eligible && <Badge testid={`tax-badge-${item.code}`} cls="bg-amber-50 text-amber-700 border-amber-200"><Receipt className="w-3 h-3" />Crédit d'impôt</Badge>}
      </div>
      {item.description && <p className="text-sm text-slate-500 mt-1 line-clamp-1">{item.description}</p>}
      {isService && item.option_codes?.length > 0 && (
        <p className="text-xs text-slate-400 mt-1">Options : {item.option_codes.map((c) => optionNames[c] || c).join(', ')}</p>
      )}
    </div>
    <div className="flex items-center gap-4 text-sm text-slate-600 shrink-0">
      <span className="font-semibold whitespace-nowrap" data-testid={`price-${item.code}`}>{formatPrice(item)}</span>
      <span className="inline-flex items-center gap-1 whitespace-nowrap" data-testid={`duration-${item.code}`}><Clock className="w-3.5 h-3.5" />{formatDuration(item.duration_minutes)}</span>
      <Button size="sm" variant="outline" onClick={() => onEdit(item)} data-testid={`edit-${item.code}`} className="gap-1"><Edit2 className="w-3.5 h-3.5" />Modifier</Button>
      <Button size="sm" variant="ghost" onClick={() => onToggle(item)} data-testid={`toggle-${item.code}`} className="gap-1">
        {item.active ? <><EyeOff className="w-3.5 h-3.5" />Désactiver</> : <><Eye className="w-3.5 h-3.5" />Activer</>}
      </Button>
    </div>
  </div>
);

const Section = ({ title, subtitle, items, isService, optionNames, onNew, onEdit, onToggle, testid }) => (
  <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={testid}>
    <div className="flex items-center justify-between gap-4 p-4 border-b border-slate-200">
      <div>
        <h2 className="font-bold text-slate-900">{title} <span className="text-slate-400 font-normal">({items.length})</span></h2>
        <p className="text-xs text-slate-500">{subtitle}</p>
      </div>
      <Button size="sm" onClick={onNew} className="gap-1.5 bg-[hsl(207,90%,54%)] hover:bg-[hsl(207,90%,46%)]" data-testid={`${testid}-new-button`}>
        <Plus className="w-4 h-4" />Ajouter
      </Button>
    </div>
    {items.map((it) => (
      <ItemRow key={it.id} item={it} isService={isService} optionNames={optionNames} onEdit={onEdit} onToggle={onToggle} />
    ))}
  </section>
);

const ServicesAdmin = () => {
  const [services, setServices] = useState([]);
  const [options, setOptions] = useState([]);
  const [editing, setEditing] = useState(null); // { kind, item }

  const load = useCallback(async () => {
    try {
      const [s, o] = await Promise.all([catalogApi.list('services'), catalogApi.list('service-options')]);
      setServices(s.data);
      setOptions(o.data);
    } catch { toast.error('Erreur de chargement des prestations'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    const { kind, item } = editing;
    try {
      if (item?.id) await catalogApi.update(kind, item.id, payload);
      else await catalogApi.create(kind, payload);
      toast.success('Enregistré');
      setEditing(null);
      load();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const toggle = (kind) => async (item) => {
    try {
      await catalogApi.update(kind, item.id, { active: !item.active });
      toast.success(item.active ? 'Désactivée' : 'Activée');
      load();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const optionNames = Object.fromEntries(options.map((o) => [o.code, o.name]));

  return (
    <div className="space-y-6" data-testid="services-admin">
      <div className="rounded-xl bg-blue-50 border border-blue-100 px-4 py-3 text-sm text-blue-800" data-testid="catalog-info-banner">
        Ce catalogue alimente le formulaire du site public : les prestations actives y sont proposées, et celles marquées « Réservation directe » (avec une durée) affichent des créneaux réservables.
      </div>
      <Section title="Prestations" subtitle="Prix, durée, réservation directe et options" items={services} isService
        optionNames={optionNames} onNew={() => setEditing({ kind: 'services', item: null })}
        onEdit={(item) => setEditing({ kind: 'services', item })} onToggle={toggle('services')} testid="services-section" />
      <Section title="Options complémentaires" subtitle="Proposées en supplément d'une prestation" items={options}
        optionNames={optionNames} onNew={() => setEditing({ kind: 'service-options', item: null })}
        onEdit={(item) => setEditing({ kind: 'service-options', item })} onToggle={toggle('service-options')} testid="options-section" />
      <CatalogItemDialog open={!!editing} item={editing?.item} isService={editing?.kind === 'services'}
        options={options} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
};

export default ServicesAdmin;
